import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams } from 'react-router-dom';

import { ReactComponent as NextArrow } from '../../../assets/icons/next-arrow.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import ConfirmBox from '../../../shared-components/modal-box/ConfirmBox';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import accountApi from '../../../utils/api/account';

interface SettingAccountProperties {
  setShowSettings: React.Dispatch<React.SetStateAction<boolean>>;
}
/**
 * Setting Account pages
 * @param {any} param0 - The param0
 * @param {any} param0.setShowSettings - The setShowSettings prop
 * @returns {any} The result
 */
function SettingAccount({ setShowSettings }: SettingAccountProperties): JSX.Element {
  const { t, i18n } = useTranslation(['account', 'userRole']);
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const accountCustomId = id || searchParameters.get('accountCustomId');

  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    content: <div />,
    header: false,
    rightCorner: '',
  });

  const [resetLoading, setResetLoading] = useState(false);

  const [confirmBox, setConfirmBox] = useState<{
    status: boolean;
    title: string;
    subTitle: string;
    submitLabel: string;
    conformSubmit: () => void;
  }>({
    status: false,
    title: '',
    subTitle: '',
    submitLabel: t('ok'),
    /**
     * Demo function
     * @returns {void}
     */
    conformSubmit: () => {},
  });

  /**
   * Sends a password reset link to the user's email.
   * @returns {void}
   */
  const facilityConfirmResetPassword = () => {
    if (!accountCustomId) {
      dispatch(
        setNotificationMessage({
          message: t('invalidTenantId'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }
    setResetLoading(true);
    accountApi
      .facilityRestPassword(accountCustomId, { submissionStatus: 'resetPassword' })
      .then(() => {
        setConfirmBox((previous) => ({ ...previous, status: false }));
        setModalView((previous) => ({ ...previous, status: false }));
        setShowSettings(false);
        dispatch(
          setNotificationMessage({
            message: t('passwordResetLinkSent'),
            status: true,
            type: 'success',
            code: 200,
          }),
        );
      })
      .catch((error) => {
        setShowSettings(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('passwordResetFailed'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      })
      .finally(() => {
        setResetLoading(false);
      });
  };

  /**
   * Opens the reset password confirmation prompt
   * @returns {void}
   */
  const resetPassword = () => {
    setConfirmBox({
      status: true,
      title: t('confirmResetTitle'),
      subTitle: t('confirmResetSubtitle'),
      /**
       * passing apiMethod to confirm rest password
       * @returns {void}
       */
      conformSubmit: () => facilityConfirmResetPassword(), // pass a function, don’t execute immediately
      submitLabel: t('proceed'),
    });
  };

  return (
    <div className="settings-card" dir={i18n.dir()}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => {
          setModalView((previous) => ({ ...previous, status: false }));
          setShowSettings(false);
        }}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
      />

      <ConfirmBox
        open={confirmBox.status}
        title={confirmBox.title}
        subTitle={confirmBox.subTitle}
        conformSubmit={confirmBox.conformSubmit}
        closeModal={() => {
          setConfirmBox((previous) => ({ ...previous, status: false }));
          setShowSettings(false);
        }}
        submitLabel={confirmBox.submitLabel}
        loading={resetLoading}
      />

      <div className="password">
        <MyButton
          label={t('resetPassword')}
          buttonType="none"
          onClickFunc={() => resetPassword()}
          svgBackIcon={<NextArrow />}
        />
      </div>

      {/* <div className="add">
        <MyButton
          label="Add User"
          buttonType="none"
          onClickFunc={() => {
          }}
          svgBackIcon={<NextArrow />}
        />
      </div> */}
    </div>
  );
}

export default SettingAccount;
